import {
  type InfiniteData,
  hashKey,
  infiniteQueryOptions,
  queryOptions,
} from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import type { Generation } from '@/lib/queries/generations'
import { isUuid } from '@/lib/short-uuid'
import { supabase } from '@/lib/supabase/client'

// =============================================================================
// Types
// =============================================================================

export type Asset = Database['public']['Tables']['assets']['Row']

export interface AssetWithUploader extends Asset {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
  generation: Generation | null
}

const ASSET_SELECT =
  '*, profiles!uploaded_by(id, email, full_name, avatar_url), generation:generations!generation_id(*, profiles!author(id, email, full_name, avatar_url))'

export interface AssetPage {
  assets: AssetWithUploader[]
  totalCount: number
}

/** The drive a workspace sees: its own uploads plus the organisation's. */
export interface AssetScope {
  orgId: string
  workspaceId: string
}

/** What part of the drive to list. */
export type AssetWhere =
  | { kind: 'folder'; folderId: string | null }
  /** Everything inside a set of folders, for a subtree download. */
  | { kind: 'folders'; folderIds: string[] }
  | { kind: 'search'; searchQuery: string }

export type AssetSort = 'name-asc' | 'name-desc' | 'newest' | 'oldest'

/** Which side of the drive to list: both, the organisation's, or the workspace's. */
export type AssetScopeFilter = 'all' | 'org' | 'workspace'

export interface AssetFilters {
  sort?: AssetSort
  scope?: AssetScopeFilter
  /** Only uploads by this member. */
  uploadedBy?: string | null
}

/** One folder's uploads on one side of the drive, already summed. */
export interface AssetSizeRow {
  folder_id: string | null
  workspace_id: string | null
  /** Bytes of the files counted, null when none has a known size. */
  file_size: number | null
  files: number
}

/** What the server says when a row is not the caller's to change or remove. */
export const SHARED_ITEM_LOCKED =
  'Only the workspace that shared this, or an organisation admin, can change it'

const PAGE_SIZE = 24
/** PostgREST returns at most this many rows per request. */
const MAX_ROWS = 1000
/** Ids travel in the query string, so a request names at most this many. */
const IDS_PER_REQUEST = 200

// =============================================================================
// Helpers
// =============================================================================

// The workspace id is spliced into a filter string, so it must be an id.
function assertScope(scope: AssetScope) {
  if (!isUuid(scope.orgId) || !isUuid(scope.workspaceId)) {
    throw new Error(
      'Invalid scope: cannot fetch assets without org and workspace IDs'
    )
  }
}

/** Uploads only, live, in the workspace or at organisation level. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyFilters<Q extends { eq: any; is: any; or: any }>(
  query: Q,
  scope: AssetScope,
  filters?: AssetFilters
): Q {
  let scoped = query
    .eq('org_id', scope.orgId)
    .is('deleted_at', null)
    .is('generation_id', null)
  if (filters?.scope === 'org') {
    scoped = scoped.is('workspace_id', null)
  } else if (filters?.scope === 'workspace') {
    scoped = scoped.eq('workspace_id', scope.workspaceId)
  } else {
    scoped = scoped.or(
      `workspace_id.eq.${scope.workspaceId},workspace_id.is.null`
    )
  }
  return filters?.uploadedBy
    ? scoped.eq('uploaded_by', filters.uploadedBy)
    : scoped
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyWhere<Q extends { eq: any; is: any; in: any; ilike: any }>(
  query: Q,
  where: AssetWhere
): Q {
  if (where.kind === 'folder') {
    return where.folderId
      ? query.eq('folder_id', where.folderId)
      : query.is('folder_id', null)
  }
  if (where.kind === 'folders') {
    return query.in('folder_id', where.folderIds)
  }
  const term = where.searchQuery.trim()
  return term.length >= 2 ? query.ilike('name', `%${term}%`) : query
}

/** Reads a whole listing page by page, past the per-request row cap. */
async function fetchEveryPage<T>(
  page: (
    from: number,
    to: number
  ) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
  what: string
): Promise<T[]> {
  const rows: T[] = []
  for (let from = 0; ; from += MAX_ROWS) {
    const { data, error } = await page(from, from + MAX_ROWS - 1)
    if (error) {
      throw new Error(`Failed to fetch ${what}: ${error.message}`, {
        cause: error,
      })
    }
    rows.push(...(data ?? []))
    if (!data || data.length < MAX_ROWS) {
      return rows
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applySort<Q extends { order: any }>(query: Q, sort?: AssetSort): Q {
  const sorted = (() => {
    switch (sort) {
      case 'name-asc':
        return query.order('name', { ascending: true })
      case 'name-desc':
        return query.order('name', { ascending: false })
      case 'oldest':
        return query.order('created_at', { ascending: true })
      default:
        return query.order('created_at', { ascending: false })
    }
  })()
  // Equal names or timestamps would otherwise shuffle between pages.
  return sorted.order('id', { ascending: true })
}

/**
 * A subtree read names its folders in the query string, so a large subtree
 * is read in several requests and joined.
 */
function whereBatches(where: AssetWhere): AssetWhere[] {
  if (where.kind !== 'folders' || where.folderIds.length <= IDS_PER_REQUEST) {
    return [where]
  }
  const batches: AssetWhere[] = []
  for (let from = 0; from < where.folderIds.length; from += IDS_PER_REQUEST) {
    batches.push({
      kind: 'folders',
      folderIds: where.folderIds.slice(from, from + IDS_PER_REQUEST),
    })
  }
  return batches
}

/**
 * The cache key of one listing, before its sort and filters. The side of
 * the drive is part of it: the other side's rows must not stand in while a
 * space switch loads.
 */
function listingKey(
  scope: AssetScope,
  part: 'list' | 'ids',
  where: AssetWhere,
  side: AssetScopeFilter = 'all'
) {
  return ['assets', 'workspace', scope.workspaceId, part, side, where] as const
}

/**
 * While a listing re-sorts or re-filters, its previous rows stand in so the
 * page never blinks empty; another folder or search still starts blank.
 */
function keepWithinListing<T>(listing: readonly unknown[]) {
  return (
    previous: T | undefined,
    previousQuery: { queryKey: readonly unknown[] } | undefined
  ) =>
    previousQuery &&
    hashKey(previousQuery.queryKey.slice(0, listing.length)) ===
      hashKey(listing)
      ? previous
      : undefined
}

// =============================================================================
// Queries
// =============================================================================

export const asset = {
  /** One page of a folder's uploads, or of search results across the drive. */
  findMany: (scope: AssetScope, where: AssetWhere, filters?: AssetFilters) => {
    const listing = listingKey(scope, 'list', where, filters?.scope)
    return infiniteQueryOptions({
      queryKey: [...listing, filters],
      placeholderData:
        keepWithinListing<InfiniteData<AssetPage, number>>(listing),
      queryFn: async ({ pageParam = 0 }): Promise<AssetPage> => {
        assertScope(scope)

        // The total is counted once, with the first page.
        const { data, error, count } = await applySort(
          applyWhere(
            applyFilters(
              supabase
                .from('assets')
                .select(
                  ASSET_SELECT,
                  pageParam === 0 ? { count: 'exact' } : {}
                ),
              scope,
              filters
            ),
            where
          ),
          filters?.sort
        ).range(pageParam, pageParam + PAGE_SIZE - 1)
        if (error) {
          throw new Error(`Failed to fetch assets: ${error.message}`, {
            cause: error,
          })
        }

        return {
          assets: (data || []) as unknown as AssetWithUploader[],
          totalCount: count || 0,
        }
      },
      initialPageParam: 0,
      getNextPageParam: (_, allPages) => {
        const loaded = allPages.reduce((sum, p) => sum + p.assets.length, 0)
        return loaded < allPages[0].totalCount ? loaded : undefined
      },
      staleTime: 5 * 60 * 1000,
    })
  },

  /** Everything matching, unpaginated, for export and select-all actions. */
  fetchAll: (scope: AssetScope, where: AssetWhere, filters?: AssetFilters) =>
    queryOptions({
      queryKey: [
        'assets',
        'workspace',
        scope.workspaceId,
        'fetch-all',
        where,
        filters,
      ],
      queryFn: async (): Promise<AssetWithUploader[]> => {
        assertScope(scope)
        const rows: unknown[] = []
        for (const batch of whereBatches(where)) {
          rows.push(
            ...(await fetchEveryPage(
              (from, to) =>
                applySort(
                  applyWhere(
                    applyFilters(
                      supabase.from('assets').select(ASSET_SELECT),
                      scope,
                      filters
                    ),
                    batch
                  ),
                  filters?.sort
                ).range(from, to),
              'all assets'
            ))
          )
        }
        return rows as AssetWithUploader[]
      },
    }),

  /** The rows behind a set of storage paths, for a picker seeded with earlier picks. */
  findByStoragePaths: (orgId: string, storagePaths: string[]) =>
    queryOptions({
      queryKey: ['assets', 'org', orgId, 'by-path', storagePaths],
      queryFn: async (): Promise<AssetWithUploader[]> => {
        const rows: unknown[] = []
        for (
          let from = 0;
          from < storagePaths.length;
          from += IDS_PER_REQUEST
        ) {
          const { data, error } = await supabase
            .from('assets')
            .select(ASSET_SELECT)
            .eq('org_id', orgId)
            .is('deleted_at', null)
            .in(
              'storage_path',
              storagePaths.slice(from, from + IDS_PER_REQUEST)
            )
          if (error) {
            throw new Error(`Failed to fetch assets: ${error.message}`, {
              cause: error,
            })
          }
          rows.push(...(data ?? []))
        }
        return rows as AssetWithUploader[]
      },
      staleTime: 5 * 60 * 1000,
    }),

  /** Every file id a listing holds, in listing order, for actions on "all files". */
  fetchIds: (scope: AssetScope, where: AssetWhere, filters?: AssetFilters) => {
    const listing = listingKey(scope, 'ids', where, filters?.scope)
    return queryOptions({
      queryKey: [...listing, filters],
      queryFn: async (): Promise<string[]> => {
        assertScope(scope)
        const rows = await fetchEveryPage<{ id: string }>(
          (from, to) =>
            applySort(
              applyWhere(
                applyFilters(
                  supabase.from('assets').select('id'),
                  scope,
                  filters
                ),
                where
              ),
              filters?.sort
            ).range(from, to),
          'asset ids'
        )
        return rows.map((row) => row.id)
      },
    })
  },

  /** Bytes and files per folder of every upload the drive can see. */
  sizes: (scope: AssetScope) =>
    queryOptions({
      queryKey: ['assets', 'workspace', scope.workspaceId, 'sizes'],
      queryFn: async (): Promise<AssetSizeRow[]> => {
        assertScope(scope)
        const { data, error } = await supabase.rpc('asset_folder_sizes', {
          p_org_id: scope.orgId,
          p_workspace_id: scope.workspaceId,
        })
        if (error) {
          throw new Error(`Failed to fetch asset sizes: ${error.message}`, {
            cause: error,
          })
        }
        return (data ?? []).map((row) => ({
          folder_id: row.folder_id,
          workspace_id: row.workspace_id,
          file_size: row.file_size,
          files: row.file_count,
        }))
      },
      staleTime: 30 * 1000,
    }),

  findOne: (assetId: string) =>
    queryOptions({
      queryKey: ['asset', assetId],
      queryFn: async (): Promise<AssetWithUploader> => {
        if (!assetId || assetId.trim() === '') {
          throw new Error(
            'Invalid assetId: cannot fetch asset without valid ID'
          )
        }

        const { data, error } = await supabase
          .from('assets')
          .select(ASSET_SELECT)
          .eq('id', assetId)
          .is('deleted_at', null)
          .single()

        if (error) {
          throw new Error(`Failed to fetch asset: ${error.message}`, {
            cause: error,
          })
        }

        return data as unknown as AssetWithUploader
      },
      staleTime: 5 * 60 * 1000,
    }),

  create: async (input: {
    orgId: string
    workspaceId: string | null
    folderId?: string | null
    name: string
    storagePath: string
    fileSize?: number
    uploadedBy: string
  }) => {
    const { data, error } = await supabase
      .from('assets')
      .insert({
        org_id: input.orgId,
        workspace_id: input.workspaceId,
        folder_id: input.folderId ?? null,
        name: input.name,
        storage_path: input.storagePath,
        file_size: input.fileSize ?? null,
        uploaded_by: input.uploadedBy,
      })
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to create asset: ${error.message}`, {
        cause: error,
      })
    }

    return data
  },

  // Rows the policies keep from the caller are skipped, not refused, so a
  // write has to count what it reached.
  rename: async (assetId: string, name: string): Promise<void> => {
    const { data, error } = await supabase
      .from('assets')
      .update({ name })
      .eq('id', assetId)
      .select('id')

    if (error) {
      throw new Error(`Failed to rename asset: ${error.message}`, {
        cause: error,
      })
    }
    if (!data || data.length === 0) {
      throw new Error(SHARED_ITEM_LOCKED)
    }
  },

  delete: async (assetId: string): Promise<void> => {
    const { refused } = await asset.deleteMany([assetId])
    if (refused > 0) {
      throw new Error(SHARED_ITEM_LOCKED)
    }
  },

  /** Soft-deletes what the caller may; the rest is reported, not thrown. */
  deleteMany: async (
    assetIds: string[]
  ): Promise<{ deleted: number; refused: number }> => {
    const deletedAt = new Date().toISOString()
    let deleted = 0
    for (let from = 0; from < assetIds.length; from += IDS_PER_REQUEST) {
      const { data, error } = await supabase
        .from('assets')
        .update({ deleted_at: deletedAt })
        .in('id', assetIds.slice(from, from + IDS_PER_REQUEST))
        .select('id')

      if (error) {
        throw new Error(`Failed to delete assets: ${error.message}`, {
          cause: error,
        })
      }
      deleted += data?.length ?? 0
    }
    return { deleted, refused: assetIds.length - deleted }
  },
}
